import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const COUNTRY_CODES = [
  ["+91", "🇮🇳"], ["+1", "🇺🇸"], ["+44", "🇬🇧"], ["+971", "🇦🇪"],
  ["+65", "🇸🇬"], ["+61", "🇦🇺"], ["+966", "🇸🇦"], ["+254", "🇰🇪"],
  ["+255", "🇹🇿"], ["+976", "🇲🇳"],
];

function PageHeader() {
  return (
    <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200" data-testid="register-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="font-hero text-xl font-bold tracking-tight" data-testid="register-header-logo">
          Stay<span className="text-blue-600">Care</span>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-3">
          <Link to="/" data-testid="register-header-rooms"
                className="hidden sm:inline-flex text-sm font-semibold text-slate-600 hover:text-slate-900 px-3 py-2 transition-colors">
            Rooms
          </Link>
          <Link to="/tenant" data-testid="register-header-tenant"
                className="text-[11px] sm:text-sm font-semibold text-slate-700 border border-slate-300 px-2.5 sm:px-5 py-2 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap">
            Tenant portal
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [note, setNote] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/public/tenant-signup", {
        name,
        phone: countryCode + phoneLocal.replace(/\D/g, ""),
        email: email || null,
        password,
        note: note || null,
      });
      toast.success("Signup received — your PG owner will assign a room shortly.");
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-dvh flex flex-col bg-slate-50">
        <PageHeader />
        <div className="flex-1 grid md:grid-cols-2">
        <div className="bg-blue-600 text-white p-10 flex flex-col justify-between order-1 md:order-2">
          <div className="font-display text-2xl font-black tracking-tighter">StayCare</div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] opacity-80 mb-3">You're on the list</div>
            <h1 className="font-display text-4xl font-black tracking-tighter leading-tight">
              Pending approval
            </h1>
            <p className="mt-5 opacity-90">
              Your PG owner will see your request in their dashboard, assign a room, and activate your account. You can log in from the <strong>Tenant portal</strong> once that's done.
            </p>
          </div>
          <div className="text-sm opacity-70">We never share your password.</div>
        </div>
        <div className="p-8 md:p-16 flex items-center order-2 md:order-1">
          <div className="w-full max-w-md space-y-6" data-testid="signup-success">
            <div>
              <h2 className="font-display text-3xl font-black tracking-tighter">Thanks, {name || "friend"}!</h2>
              <p className="text-slate-500 mt-2">
                We received your signup. Expect an SMS or a word from your PG owner once your room is assigned.
              </p>
            </div>
            <Link to="/tenant" data-testid="go-tenant-portal"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 font-medium hover:bg-blue-700">
              Go to Tenant portal <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col bg-slate-50">
      <PageHeader />
      <div className="flex-1 grid md:grid-cols-2">
      <div className="p-8 md:p-16 flex items-center">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tighter">Sign up as a tenant</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Fill in your details — we’ll assign you a room.
            </p>
            <p className="text-slate-500 mt-1 text-sm">
              Already assigned? <Link className="text-blue-600 hover:underline" to="/tenant" data-testid="tenant-login-link">Sign in to Tenant portal</Link>
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 block mb-2">Full name</label>
            <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
                   className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                   placeholder="Your name" />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 block mb-2">Phone number</label>
            <div className="flex gap-2">
              <select data-testid="register-country-code" value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-24 border border-slate-300 px-2 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                {COUNTRY_CODES.map(([c, flag]) => (
                  <option key={c} value={c}>{flag} {c}</option>
                ))}
              </select>
              <input data-testid="register-phone" required type="tel" inputMode="numeric" maxLength={14}
                     value={phoneLocal} onChange={(e) => setPhoneLocal(e.target.value.replace(/\D/g, ""))}
                     className="flex-1 border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                     placeholder="9876543210" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 block mb-2">Email <span className="text-slate-400 normal-case">(optional)</span></label>
            <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                   placeholder="you@example.com" />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 block mb-2">Password</label>
            <div className="relative">
              <input data-testid="register-password" required minLength={6} type={showPw ? "text" : "password"}
                     value={password} onChange={(e) => setPassword(e.target.value)}
                     className="w-full border border-slate-300 px-4 py-3 pr-11 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                     placeholder="At least 6 characters" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                      data-testid="register-toggle-password"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-slate-400 hover:text-slate-700">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">You'll use this to log in to the Tenant portal.</p>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 block mb-2">Note to the owner <span className="text-slate-400 normal-case">(optional)</span></label>
            <textarea data-testid="register-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                      placeholder="PG name, preferred room, when you want to move in…" />
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3" data-testid="register-error">{error}</div>}

          <button data-testid="register-submit" disabled={loading}
                  className="w-full bg-blue-600 text-white py-3.5 font-medium hover:bg-blue-700 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading ? "Submitting…" : <>Sign up <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      </div>

      <div className="bg-slate-900 text-white p-10 hidden md:flex flex-col items-center justify-center text-center gap-10">
        <Link to="/" data-testid="register-logo-home"
              className="font-display text-2xl font-black tracking-tighter hover:opacity-80 transition-opacity">
          Stay<span className="text-blue-500">Care</span>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-blue-400 mb-3">Tenant signup</div>
          <h2 className="font-display text-4xl font-black tracking-tighter leading-tight">
            Sign up today,<br />move in tomorrow.
          </h2>
        </div>
      </div>
      </div>
    </div>
  );
}
